import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabase';
import { 
    getUnreadNotifications, 
    markNotificationAsRead, 
    markAllNotificationsAsRead,
    deleteNotification as deleteNotificationService
} from '../services/notifications';

export const useNotifications = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const channelRef = useRef(null);

    // Fetch notifications from database
    const fetchNotifications = useCallback(async () => {
        if (!user?.id) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const data = await getUnreadNotifications(user.id);
            setNotifications(data || []);
        } catch (error) {
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    // Load notifications on mount
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // Subscribe to new notifications
    useEffect(() => {
        if (!user?.id) return;

        // Clean up previous subscription
        if (channelRef.current) {
            supabase.removeChannel(channelRef.current);
            channelRef.current = null;
        }

        // Subscribe to new notifications for this user
        const channel = supabase
            .channel(`notifications:${user.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    const newNotification = payload.new;
                    
                    // Don't notify if user is viewing messages page and it's a message notification
                    const isOnMessagesPage = window.location.pathname.includes('/messages');
                    if (isOnMessagesPage && newNotification.type === 'message') {
                        const activeId = location.state?.openConversationId || null;
                        if (activeId && newNotification.data?.conversation_id === activeId) {
                            return;
                        }
                    }

                    // Add to notifications list
                    setNotifications(prev => {
                        // Avoid duplicates
                        if (prev.some(n => n.id === newNotification.id)) {
                            return prev;
                        }
                        return [newNotification, ...prev].slice(0, 50);
                    });

                    // Show browser notification if permission granted
                    if ('Notification' in window && Notification.permission === 'granted') {
                        new Notification(newNotification.title, {
                            body: newNotification.message,
                            icon: '/DJ icon.webp'
                        });
                    }
                }
            )
            .subscribe();

        channelRef.current = channel;

        // Request notification permission on mount
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }

        return () => {
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
            }
        };
    }, [user?.id, location.pathname, location.state?.openConversationId]);

    // Mark notification as read
    const markAsRead = useCallback(async (notificationId = null) => {
        if (!user?.id) return;

        if (notificationId) {
            await markNotificationAsRead(notificationId, user.id);
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
        } else {
            await markAllNotificationsAsRead(user.id);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        }
    }, [user?.id]);

    // Delete notification
    const deleteNotification = useCallback(async (notificationId) => {
        if (!user?.id) return;

        const success = await deleteNotificationService(notificationId, user.id);
        if (success) {
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
        }
    }, [user?.id]);

    // Transform notifications to message format for compatibility
    const unreadMessages = notifications
        .filter(n => n.type === 'message' && !n.read)
        .map(n => ({
            id: n.id,
            conversation_id: n.data?.conversation_id,
            sender_id: n.data?.sender_id,
            sender_name: n.data?.sender_name || n.message,
            content: n.data?.content || n.message,
            created_at: n.created_at
        }));

    const unreadCount = notifications.filter(n => !n.read).length;

    return {
        notifications,
        unreadMessages,
        unreadCount,
        loading,
        markAsRead,
        deleteNotification,
        refreshNotifications: fetchNotifications
    };
};

